import { Head, Link } from "@inertiajs/react"
import { ChevronRight, CircleHelp, ExternalLink, ShieldCheck } from "lucide-react"
import {
  FerryPortSchedule,
  FerryPortWebcams,
  type FerryPortData,
} from "@/components/FerryPortDetails"
import { MapCanvas } from "@/components/MapCanvas"
import { PublicShell } from "@/components/PublicShell"
import {
  localePathsFrom,
  publicMetaDescription,
  type AlternateLink,
  type PublicLocale,
} from "@/lib/public-links"

type Verification =
  | { status: "unverified"; aspect: string; visitor_critical: boolean }
  | {
      status: "verified"
      aspect: string
      source_name: string
      source_url: string | null
      confidence: "high" | "medium" | "low"
      verified_at: string
    }

type Route = {
  name: string
  summary: string
  kind: string
  operator_name: string | null
  official_url: string
  stops: { id: number; name: string; path: string | null; coordinates: [number, number] }[]
  notes: {
    kind: "schedule" | "seasonal" | "price"
    value: string
    verification: Verification
  }[]
}

const COPY = {
  en: {
    home: "Home",
    transport: "Transport",
    stops: "Stops",
    official: "View official timetable",
    schedule: "Schedule",
    seasonal: "Seasonal information",
    price: "Prices",
    unverified: "Not yet verified",
    verified: "Verified",
  },
  de: {
    home: "Startseite",
    transport: "Verkehr",
    stops: "Haltestellen",
    official: "Offiziellen Fahrplan ansehen",
    schedule: "Fahrplan",
    seasonal: "Saisonhinweise",
    price: "Preise",
    unverified: "Noch nicht überprüft",
    verified: "Überprüft",
  },
} satisfies Record<PublicLocale, Record<string, string>>

export default function Show({
  locale,
  route,
  ferry_port,
  alternate_links,
  maptiler_key,
}: {
  locale: PublicLocale
  route: Route
  ferry_port: FerryPortData | null
  alternate_links: AlternateLink[]
  maptiler_key: string | null
}) {
  const copy = COPY[locale]
  const coordinates = route.stops.map((stop) => stop.coordinates)
  const description = publicMetaDescription(route.summary, `${route.name} transport route and official timetable.`)

  return (
    <>
      <Head title={route.name}>
        <meta name="description" content={description} />
        <meta property="og:title" content={route.name} />
        <meta property="og:description" content={description} />
        {alternate_links.map((link) => (
          <link key={link.href_lang} rel="alternate" hrefLang={link.href_lang} href={link.href} />
        ))}
      </Head>
      <PublicShell locale={locale} localePaths={localePathsFrom(alternate_links)}>
        <main className="public-container py-6">
          <nav className="text-sm text-ink-muted">
            <a href={`/${locale}`}>{copy.home}</a> <span>›</span> {copy.transport}
          </nav>

          {ferry_port && <FerryPortWebcams locale={locale} ferryPort={ferry_port} />}

          <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_22rem]">
            <section>
              <p className="public-kicker">{route.kind} · {route.operator_name}</p>
              <h1 className="public-page-title mt-2">{route.name}</h1>
              <p className="public-lede mt-3">{route.summary}</p>
              <MapCanvas
                maptilerKey={maptiler_key}
                center={coordinates[0]}
                points={coordinates}
                className="mt-6 h-[26rem]"
              />
            </section>
            <aside>
              <a href={route.official_url} target="_blank" rel="noreferrer" className="btn btn-primary w-full">
                {copy.official}
                <ExternalLink className="h-4 w-4" />
              </a>
              <h2 className="public-section-title mt-6">{copy.stops}</h2>
              <ol className="public-panel mt-3 divide-y divide-hairline">
                {route.stops.map((stop, index) => (
                  <li key={stop.id}>
                    {stop.path ? (
                      <Link
                        href={stop.path}
                        className="group flex items-center gap-3 p-4 no-underline transition-colors hover:bg-accent-faded focus-visible:bg-accent-faded"
                      >
                        <StopNumber number={index + 1} />
                        <span className="flex-1">{stop.name}</span>
                        <ChevronRight
                          className="h-4 w-4 text-ink-muted transition-transform group-hover:translate-x-0.5 group-hover:text-accent"
                          aria-hidden
                        />
                      </Link>
                    ) : (
                      <span className="flex items-center gap-3 p-4">
                        <StopNumber number={index + 1} />
                        {stop.name}
                      </span>
                    )}
                  </li>
                ))}
              </ol>
            </aside>
          </div>

          {ferry_port && (
            <FerryPortSchedule
              locale={locale}
              ferryPort={ferry_port}
              showRouteDetails={false}
            />
          )}

          <section className="mt-8 grid gap-4 md:grid-cols-3">
            {route.notes.map((note) => (
              <article key={note.kind} className="public-panel p-5">
                <h2 className="public-card-title">{copy[note.kind]}</h2>
                <p className="mt-3 text-ink-body">{note.value}</p>
                <VerificationBadge verification={note.verification} copy={copy} />
              </article>
            ))}
          </section>
        </main>
      </PublicShell>
    </>
  )
}

function StopNumber({ number }: { number: number }) {
  return (
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-bold text-surface">
      {number}
    </span>
  )
}

function VerificationBadge({
  verification,
  copy,
}: {
  verification: Verification
  copy: (typeof COPY)[PublicLocale]
}) {
  if (verification.status === "unverified") {
    return (
      <span className="mt-4 inline-flex items-center gap-1 rounded-full border border-dashed border-signal/45 bg-signal-faded px-2.5 py-1 text-xs font-semibold text-signal">
        <CircleHelp className="h-3 w-3" />
        {copy.unverified}
      </span>
    )
  }

  return (
    <span
      className="mt-4 inline-flex items-center gap-1 rounded-full border border-verified bg-verified px-2.5 py-1 text-xs font-semibold text-surface"
      title={`${verification.source_name} · ${verification.verified_at}`}
    >
      <ShieldCheck className="h-3 w-3" />
      {copy.verified} · {verification.verified_at}
    </span>
  )
}
